import { SignupExamContextQuery } from "~/lib/graphql/client";

const DEFAULT_EXAM_SIGNUP_DESCRIPTION_SUFFIX =
  "は株式会社FUSSYが提供しており、受験にはFUSSYのユーザー登録が必要です。登録はユーザーネーム、メールアドレス、パスワードのみで完了します。";
const LEGACY_EXAM_SIGNUP_DESCRIPTION_SUFFIX =
  "はFUSSYが提供しています。受験には無料のアカウント登録が必要です（受験料は別途必要）。登録は30秒で完了し、そのまま受験に進めます。";

export const DEFAULT_EXAM_SIGNUP_DESCRIPTION = `{examName}${DEFAULT_EXAM_SIGNUP_DESCRIPTION_SUFFIX}`;
export const EXAM_SIGNUP_SUBMIT_LABEL = "登録して受験する";

export type ExamSignupContext = NonNullable<
  SignupExamContextQuery["signupExamContext"]
>;

const EXAM_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const FUSSY_ORIGIN = "https://www.fussy.fun";

export function firstQueryValue(
  value: string | string[] | undefined
): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

export function normalizeExamSlug(
  value: string | string[] | undefined
): string | undefined {
  const slug = firstQueryValue(value)?.trim().toLowerCase();
  if (!slug || slug.length > 100 || !EXAM_SLUG_PATTERN.test(slug)) {
    return undefined;
  }
  return slug;
}

export function safeInternalRedirect(
  value: string | string[] | undefined
): string | undefined {
  const path = firstQueryValue(value);
  if (
    !path ||
    path.length > 2048 ||
    !path.startsWith("/") ||
    path.startsWith("//") ||
    /[\\\u0000-\u001f\u007f]/.test(path)
  ) {
    return undefined;
  }

  try {
    let decodedPath = path;
    for (let index = 0; index < 3; index += 1) {
      if (
        decodedPath.startsWith("//") ||
        /[\\\u0000-\u001f\u007f]/.test(decodedPath)
      ) {
        return undefined;
      }

      const decodedUrl = new URL(decodedPath, FUSSY_ORIGIN);
      if (
        decodedUrl.origin !== FUSSY_ORIGIN ||
        decodedUrl.pathname.startsWith("//")
      ) {
        return undefined;
      }

      const nextDecodedPath = decodeURIComponent(decodedPath);
      if (nextDecodedPath === decodedPath) break;
      decodedPath = nextDecodedPath;
    }

    const url = new URL(path, FUSSY_ORIGIN);
    if (url.origin !== FUSSY_ORIGIN || url.pathname.startsWith("//")) {
      return undefined;
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return undefined;
  }
}

export function examDestination(context: ExamSignupContext): string {
  return `/category/${encodeURIComponent(context.categoryId)}/exam/${encodeURIComponent(
    context.internalId
  )}`;
}

export function authDestination(
  context: ExamSignupContext | null | undefined,
  redirect: string | string[] | undefined,
  fallback: string
): string {
  if (context) return examDestination(context);
  return safeInternalRedirect(redirect) ?? fallback;
}

export function buildAccountHref(
  path: "/account/login" | "/account/signup",
  params: {
    examSlug?: string;
    redirect?: string;
  }
): string {
  const searchParams = new URLSearchParams();
  const examSlug = normalizeExamSlug(params.examSlug);
  if (examSlug) searchParams.set("exam_slug", examSlug);

  const redirect = safeInternalRedirect(params.redirect);
  if (redirect) searchParams.set("redirect", redirect);

  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
}

export function examSignupDescription(context: ExamSignupContext): string {
  const description = context.examDesc?.trim();

  // 以前の標準文言が個別説明として保存されている場合、検定名の変更後も
  // 古い検定名が表示されないよう、現在の検定名で組み立て直す。
  if (
    !description ||
    description.endsWith(DEFAULT_EXAM_SIGNUP_DESCRIPTION_SUFFIX) ||
    description.endsWith(LEGACY_EXAM_SIGNUP_DESCRIPTION_SUFFIX)
  ) {
    return DEFAULT_EXAM_SIGNUP_DESCRIPTION.replace(
      "{examName}",
      context.examName
    );
  }

  return description;
}

export function examSignupHeading(context: ExamSignupContext): string {
  return `${context.examName}の受験には、アカウント登録（無料）が必要です`;
}

export function examSignupFreeAccountCopy(context: ExamSignupContext): string {
  return `ユーザー登録は無料です。${context.examName}の受験料以外の費用は発生しません`;
}

export function examSignupDestinationCopy(context: ExamSignupContext): string {
  return `登録完了後、自動的に${context.examName}のページに移動します`;
}
